import React, {Component} from 'react';
import styled from "styled-components";
import PropTypes from 'prop-types';
import Vimeo from '@u-wave/react-vimeo';

import TextFormat from '../common/TextFormat.jsx';
import Utils from '../common/Utils.jsx';

import VideoUpNextCard from './VideoUpNextCard.jsx';
import VideoInfoBlock from './VideoInfoBlock.jsx';
import VideoNavigation from './VideoNavigation.jsx';
import SidebarLicense from './SidebarLicense.jsx';
import SidebarDisclaimer from './SidebarDisclaimer.jsx';
import SidebarSocial from './SidebarSocial.jsx';

const VIDEO_WIDTH_PCT = 0.46;
const UP_NEXT_WIDTH_PCT = 0.15;
const TOTAL_UPNEXT = 17;
const LARGE_UPNEXT = 5;
const WRAPPER_FADE_IN = 1618;

const ContentWrapper = styled.div`
    vertival-align: top;
    margin: 1rem auto;
    text-align: center;
    border: 0.15rem solid #ececec;
    box-shadow: 0.5rem 0.5rem 1.0rem #f4f4f4;
    border-radius: 0.25rem;
    padding-bottom: 1rem;
`;

const IFrameWrapper = styled.div`
    display: inline-block;
    margin: auto;
    width: ${VIDEO_WIDTH_PCT * window.innerWidth}px;
`;

const TitleBar = styled.div`
    width: 100%;
    height: 100%;
    text-align: center;
`;

const TitleText = styled.div`
    font-size: 3.5rem;
    letter-spacing: 2.0rem;
    color: #777777;
    text-shadow: 0.5rem 0.75rem 1.0rem #dddddd;
    margin: 1rem;
`;

const UpNextPanel = styled.div`
    display: inline-block;
    vertical-align: top;
    margin: 0;    
    background-color: #f4f4f4;
    width: ${UP_NEXT_WIDTH_PCT * window.innerWidth}px;
`;

const SectionText = styled.div`
    font-size: 1.5rem;
    font-style: italic;
    text-align: center;
    padding-right: 1rem;
    margin: 1.5rem auto 1.75rem;
    color: #aaaaaa;
    width: 92%;    
`;

const SeeThru = styled.div`
    opacity: 0;  
    height: auto;  
`;

export class VideoSetPage extends Component {

    static propTypes = {
        featured_section: PropTypes.string.isRequired,
        sections: PropTypes.object.isRequired,
        explainers: PropTypes.object.isRequired,
        narrative: PropTypes.object.isRequired,
        extra: PropTypes.element.isRequired,
        on_reference_click: PropTypes.func.isRequired,
        references: PropTypes.object.isRequired
    }

    state = {
        video_obj: null,
        frame_width: 1,
        frame_height: 1,
        video_index: 0,
        video_ready: false,
        video_items: [],
        wrapper_ref: React.createRef()
    };

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
        const {sections, featured_section} = this.props;
        const featured = sections[featured_section]
        if (featured) {
            let video_items = [];
            for (const item_name in featured.items) {
                const item = featured.items[item_name];
                item.index = Math.random();
                video_items.push(item);
            }
            video_items.sort((a, b) => a.index - b.index);
            this.setState({video_items: video_items});
            console.log("video_items")
        }
    }

    on_ready(video_obj) {
        const {wrapper_ref} = this.state
        const frame_width_px = Math.floor(window.innerWidth * VIDEO_WIDTH_PCT);
        const frame_height_px = Math.floor((frame_width_px * 9) / 16);
        this.setState({
            video_obj: video_obj,
            frame_width: `${frame_width_px}px`,
            frame_height: `${frame_height_px}px`,
            video_ready: true
        });
        Utils.animate(wrapper_ref, WRAPPER_FADE_IN, 0, 1, value => {
            wrapper_ref.current.style.opacity = value;
        });
    }

    on_progress(data) {
        // console.log("on_progress", data)
    }

    on_end(data) {
        console.log("on_end", data);
        const next_index = (this.state.video_index + 1) % this.state.video_items.length;
        this.setState({video_index: next_index})
    }

    on_featured = (video_item_index) => {
        this.setState({video_index: video_item_index})
        document.getElementById("title").scrollIntoView({behavior: 'smooth'});
    }

    render() {
        const {video_ready, video_items, video_index, frame_width, frame_height, wrapper_ref} = this.state;
        const {narrative, explainers, featured_section, on_reference_click, references} = this.props;

        if (!video_items.length) {
            return '...'
        }

        const video_obj = !video_items.length ? '' :
            <Vimeo
                tabindex={0}
                video={video_items[video_index]["vimeo-id"]}
                width={frame_width}
                height={frame_height}
                dnt muted autoplay
                onReady={video_obj => this.on_ready(video_obj)}
                onProgress={data => this.on_progress(data)}
                onEnd={data => this.on_end(data)}
                style={{backgroundColor: '#eeeeee'}}/>

        const videos_next = [];
        for (let index = 0; index < Math.min(video_items.length, TOTAL_UPNEXT); index++) {
            const video_item_index = (video_index + index + 1) % video_items.length;
            if (video_item_index === video_index) {
                continue;
            }
            videos_next.push(<VideoUpNextCard
                key={"up_next_" + index}
                on_click={() => this.on_featured(video_item_index)}
                video_item={video_items[video_item_index]}
                half_size={index >= LARGE_UPNEXT}/>)
        }

        let narratives = [];
        const narrative_keys = Object.keys(narrative)
        narrative_keys.forEach(key => {
            narratives.push(narrative[key])
        });
        const shuffled = Utils.shuffle(narratives);
        const section_text = <SectionText>
            <TextFormat text={shuffled[0].value} is_first={false} of_many={false}/>
        </SectionText>

        const see_thru_style = {display: video_ready ? 'block' : 'none'}
        return <ContentWrapper>

            <TitleBar id={"title"}>
                <TitleText>{featured_section}</TitleText>
            </TitleBar>

            <SeeThru style={see_thru_style} ref={wrapper_ref}>
                <IFrameWrapper>
                    {video_obj}
                    <VideoNavigation
                        on_change={index => this.on_featured(index)}
                        item_index={video_index}
                        item_count={video_items.length}/>
                    {section_text}
                    {this.props.extra}
                </IFrameWrapper>

                <UpNextPanel>
                    <VideoInfoBlock
                        video_item={video_items[video_index].items}
                        references={references} on_reference_click={on_reference_click}/>
                    {videos_next}
                    <SidebarDisclaimer items={explainers.disclaimers.items}/>
                    <SidebarLicense text={explainers.license.value}/>
                    <SidebarSocial url={"http://www.mikehallvideo.com"}/>
                </UpNextPanel>
            </SeeThru>

        </ContentWrapper>
    }
}

export default VideoSetPage;
